import axios from 'axios';
import client from './client';
import { allPages } from './allPages';
import type { ColourProfile, TenantLook } from './colourProfiles';

// Types
export interface TenantRole {
  tenantId: number;
  tenantName: string;
  /** The organisation the tenant belongs to: the questionnaire API is keyed by it. */
  organisationId: number;
  role: string;
}

export interface UserRoles {
  isGlobalAdmin: boolean;
  tenantRoles: TenantRole[];
  memberTenantIds: number[];
  /** Absent from an API older than colour profiles, which the app reads as Meridian. */
  looks?: TenantLook[];
  lookOutsideTenant?: ColourProfile | null;
}

export interface Tenant {
  id: number;
  name: string;
  organisationId: number;
  organisationName: string;
}

export interface TenantCreate {
  name: string;
  organisationId: number;
}

export interface TenantUpdate {
  name: string;
}

export interface Organisation {
  id: number;
  legalName: string;
  tradeName: string | null;
  taxId: string | null;
  referenceCode: string | null;
  phoneNumber: string | null;
  emailAddress: string | null;
  website: string | null;
  practiceCode: string | null;
  chamberOfCommerceNumber: string | null;
  subscriptionDate: string | null;
  isActive: boolean;
}

export interface OrganisationCreate {
  legalName: string;
  tradeName?: string | null;
  taxId?: string | null;
  referenceCode?: string | null;
  phoneNumber?: string | null;
  emailAddress?: string | null;
  website?: string | null;
  practiceCode?: string | null;
  chamberOfCommerceNumber?: string | null;
  subscriptionDate?: string | null;
  isActive?: boolean;
}

export interface OrganisationUpdate {
  legalName?: string | null;
  tradeName?: string | null;
  taxId?: string | null;
  referenceCode?: string | null;
  phoneNumber?: string | null;
  emailAddress?: string | null;
  website?: string | null;
  practiceCode?: string | null;
  chamberOfCommerceNumber?: string | null;
  subscriptionDate?: string | null;
  isActive?: boolean | null;
}

export interface TenantAdmin {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
}

/** A member of one of an organisation's tenants, as the platform sees them. */
export interface OrganisationMember {
  userId: number;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  tenantId: number;
  tenantName: string;
  /** When the membership began. */
  since: string;
}

export interface SuccessResponse {
  success: boolean;
  message: string;
}

export interface UserInfo {
  id: number;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
}

export interface UserAdminInfo {
  id: number;
  email: string | null;
  status: string;
  firstName: string | null;
  lastName: string | null;
  organisationNames: string[];
}

/**
 * Retrieves role assignments for the currently authenticated user.
 * The response includes a global administrator flag and a set of tenant-scoped roles.
 */
export const getUserRoles = async (): Promise<UserRoles> => {
  const response = await client.get('/users/me/roles');
  return response.data;
};

/**
 * Retrieves tenants visible to the current administrator.
 * Pagination is supported through `skip` and `limit` query parameters.
 */
export const getTenants = async (skip = 0, limit = 100): Promise<Tenant[]> => {
  const response = await client.get('/tenants', { params: { skip, limit } });
  return response.data;
};

/**
 * One tenant, as the platform sees it, or null when there is none with that id.
 * There is no platform read of a single tenant -- the tenant's own route asks
 * for a role inside it -- so this pages through the list the tenants grid reads.
 */
export const findTenant = async (tenantId: number): Promise<Tenant | null> => {
  const pageSize = 100;
  for (let skip = 0; ; skip += pageSize) {
    const page = await getTenants(skip, pageSize);
    const found = page.find((tenant) => tenant.id === tenantId);
    if (found) return found;
    if (page.length < pageSize) return null;
  }
};

/**
 * Retrieves a single tenant by identifier.
 */
export const getTenant = async (tenantId: number): Promise<Tenant> => {
  const response = await client.get(`/tenants/${tenantId}`);
  return response.data;
};

/**
 * Creates a new tenant under a specified organisation.
 */
export const createTenant = async (data: TenantCreate): Promise<Tenant> => {
  const response = await client.post('/tenants', data);
  return response.data;
};

/**
 * Renames a tenant from the administration console.
 *
 * On the platform plane for the same reason as the administrator calls below:
 * the tenant's own PUT asks for tenant:settings:edit in that tenant, which a
 * global admin does not hold. An organisation renames itself from its own
 * settings screen, through api/tenant.ts.
 */
export const updateTenant = async (tenantId: number, data: TenantUpdate): Promise<Tenant> => {
  const response = await client.put(`/admin/tenants/${tenantId}`, data);
  return response.data;
};

/**
 * Retrieves organisations available to an administrator.
 */
export const getOrganisations = async (skip = 0, limit = 100): Promise<Organisation[]> => {
  const response = await client.get('/organizations/admin', { params: { skip, limit } });
  return response.data;
};

/**
 * Creates a new organisation using the provided administrative fields.
 */
export const createOrganisation = async (data: OrganisationCreate): Promise<Organisation> => {
  const response = await client.post('/organizations/admin', data);
  return response.data;
};

/**
 * Updates an organisation by identifier.
 */
export const updateOrganisation = async (
  organisationId: number,
  data: OrganisationUpdate,
): Promise<Organisation> => {
  const response = await client.put(`/organizations/admin/${organisationId}`, data);
  return response.data;
};

/** One organisation, or null when there is none with that id. */
export const findOrganisation = async (organisationId: number): Promise<Organisation | null> => {
  try {
    const response = await client.get(`/organizations/admin/${organisationId}`);
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) return null;
    throw err;
  }
};

/** The members of every tenant the organisation runs, one row per membership. */
export const getOrganisationMembers = async (
  organisationId: number,
  skip = 0,
  limit = 100,
): Promise<OrganisationMember[]> => {
  const response = await client.get(`/organizations/admin/${organisationId}/members`, { params: { skip, limit } });
  return response.data;
};

export const getAllOrganisationMembers = (organisationId: number): Promise<OrganisationMember[]> =>
  allPages((skip, limit) => getOrganisationMembers(organisationId, skip, limit));

/** Makes an existing account a member of a tenant, with the tenant's Member role. */
export const addTenantMember = async (tenantId: number, userId: number): Promise<OrganisationMember> => {
  const response = await client.post(`/admin/tenants/${tenantId}/members`, { userId });
  return response.data;
};

/**
 * Retrieves tenants belonging to a specified organisation.
 */
export const getTenantsByOrganisation = async (organisationId: number): Promise<Tenant[]> => {
  const response = await client.get(`/organizations/admin/${organisationId}/tenants`);
  return response.data;
};

/**
 * Retrieves tenant administrator accounts associated with a tenant.
 *
 * On the platform plane (/admin/tenants), as every call from the administration
 * console is: a global admin holds no role inside an organisation, so the
 * tenant's own route refuses them.
 */
export const getTenantAdmins = async (tenantId: number): Promise<TenantAdmin[]> => {
  const response = await client.get(`/admin/tenants/${tenantId}/admins`);
  return response.data;
};

/**
 * Assigns an existing user as a tenant administrator.
 */
export const assignTenantAdmin = async (
  tenantId: number,
  userId: number,
): Promise<SuccessResponse> => {
  const response = await client.post(`/admin/tenants/${tenantId}/admins`, { userId });
  return response.data;
};

/**
 * Takes the tenant administrator role away from a user.
 */
export const removeTenantAdmin = async (
  tenantId: number,
  userId: number,
): Promise<SuccessResponse> => {
  const response = await client.delete(`/admin/tenants/${tenantId}/admins/${userId}`);
  return response.data;
};

/**
 * Retrieves all platform users with party/organisation info. Global admin only.
 */
export const getUsers = async (skip = 0, limit = 100): Promise<UserAdminInfo[]> => {
  const response = await client.get('/users', { params: { skip, limit } });
  return response.data;
};

/** The tenant's members: the people who can be appointed its administrator. */
export const getTenantUsers = async (tenantId: number, skip = 0, limit = 100): Promise<UserInfo[]> => {
  const response = await client.get(`/admin/tenants/${tenantId}/users`, { params: { skip, limit } });
  return response.data;
};

/** All of them: an organisation's members count too. */
export const getAllTenantUsers = (tenantId: number): Promise<UserInfo[]> =>
  allPages((skip, limit) => getTenantUsers(tenantId, skip, limit));

/** Every tenant on the platform. */
export const getAllTenants = (): Promise<Tenant[]> => allPages(getTenants);

/** Every organisation, for the lists that show or offer them all. */
export const getAllOrganisations = (): Promise<Organisation[]> => allPages(getOrganisations);

/** Every user on the platform. */
export const getAllUsers = (): Promise<UserAdminInfo[]> => allPages(getUsers);
